import recordEvent from 'platform/monitoring/record-event';
import { uploadDocument, isPdfFile } from './upload';
import { ACTIVE_SECONDARY_STATUSES } from '../domain/constants';

/**
 * Initiates a CAVE (secondary) upload for a file.
 *
 * @param {File} file - The file to upload.
 * @param {string} trackingKey - The stable identity key for this file.
 * @param {object} context
 * @param {React.MutableRefObject<object>} context.statusRef - Ref whose .current
 *   maps trackingKey → last-known status string. Mutated synchronously.
 * @param {object} context.existingUploads - Snapshot of secondaryUploads state,
 *   used for dedup checks.
 * @param {function} context.onUpdate - Callback(trackingKey, payload) that
 *   merges payload into secondaryUploads state.
 */
export const startSecondaryUpload = (file, trackingKey, context) => {
  const { statusRef, existingUploads, onUpdate } = context;

  if (!file || !trackingKey) return;

  const metadata = {
    name: file.name,
    size: file.size,
    lastModified: file.lastModified,
  };

  if (!isPdfFile(file)) {
    statusRef.current[trackingKey] = 'skipped';
    onUpdate(trackingKey, {
      status: 'skipped',
      error: 'Only PDF files are sent for automated processing.',
      ...metadata,
    });
    return;
  }

  const existingState = statusRef.current[trackingKey];
  const existing = existingUploads[trackingKey];
  if (
    (existingState && ACTIVE_SECONDARY_STATUSES.has(existingState)) ||
    (existing && ACTIVE_SECONDARY_STATUSES.has(existing.status))
  ) {
    return;
  }

  statusRef.current[trackingKey] = 'pending';
  onUpdate(trackingKey, { status: 'pending', ...metadata });

  recordEvent({
    event: 'cave-secondary-upload-start',
    'api-name': 'CAVE Secondary Upload',
    'api-status': 'started',
  });

  uploadDocument(file)
    .then(contract => {
      statusRef.current[trackingKey] = 'processing';
      onUpdate(trackingKey, { status: 'processing', contract, ...metadata });
      recordEvent({
        event: 'cave-secondary-upload-success',
        'api-name': 'CAVE Secondary Upload',
        'api-status': 'successful',
      });
    })
    .catch(error => {
      statusRef.current[trackingKey] = 'error';
      onUpdate(trackingKey, {
        status: 'error',
        error: error?.message || 'Automated processing upload failed.',
        ...metadata,
      });
      recordEvent({
        event: 'cave-secondary-upload-failure',
        'api-name': 'CAVE Secondary Upload',
        'api-status': 'failed',
        // Analytics dimension must be a STABLE, bounded key. CaveError codes
        // are a safe set; never push error.message — it can carry
        // document-specific text / PII and would explode GA cardinality. The
        // full message is preserved in the per-file error state for the UI.
        'error-key': error?.code || 'unknown',
      });
    });
};
